import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingBag, 
  Package, 
  Gift, 
  Star, 
  ChevronRight,
  Plus,
  Minus,
  Check,
  Truck,
  Heart,
  Sparkles
} from 'lucide-react';

const SweetShopLanding = () => {
  const [selectedItems, setSelectedItems] = useState({});
  const [showBundleBuilder, setShowBundleBuilder] = useState(false);

  // Product data
  const fudgeVarieties = [
    { id: 'f1', name: 'Classic Chocolate', description: 'Rich, creamy traditional chocolate fudge', image: 'https://images.unsplash.com/photo-1548907040-4baa42d10919?w=400&h=300&fit=crop', popular: true },
    { id: 'f2', name: 'Peanut Butter Swirl', description: 'Chocolate and peanut butter perfection', image: 'https://images.unsplash.com/photo-1606312619070-d48b4c652a52?w=400&h=300&fit=crop', popular: true },
    { id: 'f3', name: 'Maple Walnut', description: 'Vermont maple with crunchy walnuts', image: 'https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=400&h=300&fit=crop' },
    { id: 'f4', name: 'Sea Salt Caramel', description: 'Sweet caramel with a hint of sea salt', image: 'https://images.unsplash.com/photo-1582176604856-e824b4736522?w=400&h=300&fit=crop', popular: true },
    { id: 'f5', name: 'Rocky Road', description: 'Marshmallows and almonds in chocolate', image: 'https://images.unsplash.com/photo-1553452118-621e1f860f43?w=400&h=300&fit=crop' },
    { id: 'f6', name: 'White Chocolate Raspberry', description: 'Creamy white chocolate with raspberry swirl', image: 'https://images.unsplash.com/photo-1571115177098-24ec42ed204d?w=400&h=300&fit=crop' },
  ];

  const nutVarieties = [
    { id: 'n1', name: 'Honey Roasted Cashews', description: 'Sweet and crunchy perfection', image: 'https://images.unsplash.com/photo-1536816579748-4ecb3f03d72a?w=400&h=300&fit=crop', popular: true },
    { id: 'n2', name: 'Butter Toffee Almonds', description: 'Buttery toffee coating on premium almonds', image: 'https://images.unsplash.com/photo-1608797178974-15b35a64ede9?w=400&h=300&fit=crop' },
    { id: 'n3', name: 'Cinnamon Glazed Pecans', description: 'Southern-style cinnamon sugar pecans', image: 'https://images.unsplash.com/photo-1597371424128-8ffb9082c828?w=400&h=300&fit=crop', popular: true },
    { id: 'n4', name: 'Mixed Nut Brittle', description: 'Crunchy brittle with assorted nuts', image: 'https://images.unsplash.com/photo-1599599810694-b5b37304c041?w=400&h=300&fit=crop' },
  ];

  const candyVarieties = [
    { id: 'c1', name: 'Saltwater Taffy Assortment', description: 'Classic flavors in every pull', image: 'https://images.unsplash.com/photo-1581798459219-318e76aecc7b?w=400&h=300&fit=crop', popular: true },
    { id: 'c2', name: 'Chocolate Truffles', description: 'Hand-rolled gourmet truffles', image: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=400&h=300&fit=crop', popular: true },
    { id: 'c3', name: 'Caramel Corn', description: 'Buttery caramel popcorn goodness', image: 'https://images.unsplash.com/photo-1578849278619-e73505e9610f?w=400&h=300&fit=crop' },
    { id: 'c4', name: 'Fruit Jellies', description: 'Natural fruit flavored jellies', image: 'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=400&h=300&fit=crop' },
  ];

  const sizes = [
    { weight: '1/4 lb', price: 8.99, label: 'Quarter Pound' },
    { weight: '1/2 lb', price: 15.99, label: 'Half Pound' },
    { weight: '1 lb', price: 28.99, label: 'One Pound' },
  ];

  const bundles = [
    { 
      id: 'b1', 
      name: 'Sweet Sampler', 
      description: '1/4 lb each of 4 varieties of your choice',
      price: 29.99,
      savings: 6,
      items: '4 × 1/4 lb'
    },
    { 
      id: 'b2', 
      name: 'Family Favorites', 
      description: '1/2 lb each of 3 varieties of your choice',
      price: 42.99,
      savings: 5,
      items: '3 × 1/2 lb'
    },
    { 
      id: 'b3', 
      name: 'Gift Box Deluxe', 
      description: '1 lb each of 2 varieties in a gift box',
      price: 52.99,
      savings: 5,
      items: '2 × 1 lb + Gift Box'
    },
    { 
      id: 'b4', 
      name: 'Party Pack', 
      description: 'Mix & match 3 lbs total - perfect for events',
      price: 74.99,
      savings: 12,
      items: '3 lbs total'
    },
  ];

  const ProductCard = ({ product, category }) => (
    <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {product.popular && (
          <Badge className="absolute top-3 left-3 bg-amber-500 text-white">
            <Star className="w-3 h-3 mr-1 fill-current" /> Popular
          </Badge>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <CardContent className="p-4">
        <h3 className="font-bold text-lg text-slate-900">{product.name}</h3>
        <p className="text-sm text-slate-500 mt-1">{product.description}</p>
        <div className="flex gap-2 mt-4">
          {sizes.map((size) => (
            <Button 
              key={size.weight}
              variant="outline" 
              size="sm"
              className="flex-1 text-xs hover:bg-amber-50 hover:border-amber-400 hover:text-amber-700"
            >
              {size.weight}
              <span className="ml-1 text-amber-600 font-semibold">${size.price}</span>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🍫</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Sweet Carolina Confections</h1>
              <p className="text-amber-200 text-sm">Handcrafted in Southport, NC</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:bg-amber-700">
              <Heart className="w-5 h-5 mr-2" />
              Favorites
            </Button>
            <Button className="bg-white text-amber-900 hover:bg-amber-100">
              <ShoppingBag className="w-5 h-5 mr-2" />
              Cart (0)
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-800 via-amber-700 to-amber-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-amber-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-orange-400 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-amber-500/20 text-amber-200 border-amber-400/30 mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                Now Shipping Nationwide
              </Badge>
              <h2 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
                Handcrafted <br />
                <span className="text-amber-300">Sweet Treats</span>
              </h2>
              <p className="text-xl text-amber-100 mb-8 leading-relaxed">
                From our kitchen to your door. Premium fudge, roasted nuts, and 
                artisan candies made in small batches with love.
              </p>
              <div className="flex gap-4">
                <Button size="lg" className="bg-white text-amber-900 hover:bg-amber-100 text-lg px-8">
                  Shop Now
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" className="border-amber-300 text-white hover:bg-amber-700 text-lg px-8">
                  Build a Bundle
                  <Gift className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="flex items-center gap-8 mt-10 text-amber-200">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-amber-400" />
                  <span>Free Shipping $50+</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-amber-400" />
                  <span>Made Fresh Daily</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1548907040-4baa42d10919?w=300&h=300&fit=crop" 
                  alt="Chocolate Fudge"
                  className="w-full rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform"
                />
                <img 
                  src="https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=300&h=300&fit=crop" 
                  alt="Truffles"
                  className="w-full rounded-2xl shadow-2xl transform -rotate-3 hover:rotate-0 transition-transform mt-8"
                />
                <img 
                  src="https://images.unsplash.com/photo-1536816579748-4ecb3f03d72a?w=300&h=300&fit=crop" 
                  alt="Roasted Nuts"
                  className="w-full rounded-2xl shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform"
                />
                <img 
                  src="https://images.unsplash.com/photo-1581798459219-318e76aecc7b?w=300&h=300&fit=crop" 
                  alt="Taffy"
                  className="w-full rounded-2xl shadow-2xl transform rotate-2 hover:rotate-0 transition-transform mt-4"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Size Options Banner */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <Package className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">1/4 Pound</p>
              <p className="text-sm text-slate-500">Perfect for trying</p>
            </div>
            <div className="text-center">
              <Package className="w-10 h-10 text-amber-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">1/2 Pound</p>
              <p className="text-sm text-slate-500">Great for sharing</p>
            </div>
            <div className="text-center">
              <Package className="w-12 h-12 text-amber-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">1 Pound</p>
              <p className="text-sm text-slate-500">Best value</p>
            </div>
            <div className="text-center border-l pl-8 md:pl-16">
              <Gift className="w-10 h-10 text-amber-600 mx-auto mb-2" />
              <p className="font-bold text-slate-900">Custom Bundles</p>
              <p className="text-sm text-slate-500">Mix & match favorites</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bundles Section */}
      <section className="py-16 bg-gradient-to-b from-amber-100 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-amber-200 text-amber-800 mb-4">Save More</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Bundle & Save</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Mix and match your favorites with our curated bundles. Perfect for gifts or stocking up!
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {bundles.map((bundle) => (
              <Card key={bundle.id} className="border-2 border-amber-200 hover:border-amber-400 transition-colors overflow-hidden group">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-4 text-white">
                  <div className="flex justify-between items-start">
                    <Gift className="w-8 h-8" />
                    <Badge className="bg-white text-amber-700">Save ${bundle.savings}</Badge>
                  </div>
                  <h3 className="text-xl font-bold mt-3">{bundle.name}</h3>
                  <p className="text-amber-100 text-sm mt-1">{bundle.items}</p>
                </div>
                <CardContent className="p-4">
                  <p className="text-slate-600 text-sm mb-4">{bundle.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-amber-700">${bundle.price}</span>
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      Select
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fudge Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Handmade Fudge</h2>
              <p className="text-slate-600 mt-2">Made fresh in small batches daily</p>
            </div>
            <Button variant="outline" className="hidden md:flex">
              View All Fudge <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fudgeVarieties.map((product) => (
              <ProductCard key={product.id} product={product} category="fudge" />
            ))}
          </div>
        </div>
      </section>

      {/* Nuts Section */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Roasted Nuts</h2>
              <p className="text-slate-600 mt-2">Premium nuts, perfectly seasoned</p>
            </div>
            <Button variant="outline" className="hidden md:flex">
              View All Nuts <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {nutVarieties.map((product) => (
              <ProductCard key={product.id} product={product} category="nuts" />
            ))}
          </div>
        </div>
      </section>

      {/* Candy Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Artisan Candy</h2>
              <p className="text-slate-600 mt-2">Classic favorites & gourmet treats</p>
            </div>
            <Button variant="outline" className="hidden md:flex">
              View All Candy <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {candyVarieties.map((product) => (
              <ProductCard key={product.id} product={product} category="candy" />
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-16 bg-gradient-to-r from-amber-800 to-amber-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <Truck className="w-12 h-12 mx-auto mb-4 text-amber-300" />
              <h3 className="text-xl font-bold mb-2">Free Shipping</h3>
              <p className="text-amber-200">On orders over $50. Arrives in 2-5 business days.</p>
            </div>
            <div>
              <Package className="w-12 h-12 mx-auto mb-4 text-amber-300" />
              <h3 className="text-xl font-bold mb-2">Gift Ready</h3>
              <p className="text-amber-200">Beautiful packaging. Add a personalized note!</p>
            </div>
            <div>
              <Heart className="w-12 h-12 mx-auto mb-4 text-amber-300" />
              <h3 className="text-xl font-bold mb-2">Made with Love</h3>
              <p className="text-amber-200">Small batch recipes passed down for generations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-950 text-amber-200 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🍫</span>
                <span className="text-xl font-bold text-white">Sweet Carolina</span>
              </div>
              <p className="text-sm">
                Handcrafted confections made with premium ingredients and 
                old-fashioned recipes in Southport, North Carolina.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Shop</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">All Products</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Fudge</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roasted Nuts</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Candy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Gift Bundles</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Help</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Shipping Info</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Returns & Refunds</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Stay Sweet</h4>
              <p className="text-sm mb-4">Get 10% off your first order!</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Enter email"
                  className="flex-1 px-3 py-2 rounded bg-amber-900 border border-amber-700 text-white placeholder-amber-400 text-sm"
                />
                <Button className="bg-amber-600 hover:bg-amber-500">
                  Join
                </Button>
              </div>
            </div>
          </div>
          <div className="border-t border-amber-800 mt-8 pt-8 text-center text-sm">
            <p>© 2026 Sweet Carolina Confections. All rights reserved.</p>
            <p className="mt-2 text-amber-400">🍬 Made with love in Southport, NC 🍬</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SweetShopLanding;
